import { ThreeService } from "src/app/binst/rendering/threejs/three.service";
import { Mesh, CapsuleGeometry, MeshBasicMaterial, MeshStandardMaterial, Texture, LinearMipMapLinearFilter, LinearFilter, TextureLoader, Material, Group } from "three";
import { Vector3 } from "three/src/math/Vector3.js"
import { animate, utils } from 'animejs';
import { degToRad } from "three/src/math/MathUtils.js";

import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {NetworkServices} from "../../instance-service/i-network-services";
import {SurfaceService} from "../../../surface/surface.service";
import {TouchPrimitive} from "../../../inputs/primitives/touch-primitive";
import {HasTouchPrimitive} from "../../../inputs/primitives/i-has-touch-primitive";
import {StateServices} from "../../instance-service/i-state-services";
import {interfaceActions} from "../../../../state/interface/interface.actions";
import {PointerTracker} from "../../../inputs/primitives/pointer-tracker";

export class UserAvatarDrawable implements HasTouchPrimitive {
  threeService: ThreeService
  networkServices: NetworkServices
  stateServices: StateServices
  ballContainer: Group
  ball: Mesh
  touchPrimitive: TouchPrimitive
  pointAngle = {
    axis: new Vector3(0, 0, 0),
    angle: 0
  }
  userId: string
  imageFileId: string
  debug = true

  constructor() {
    console.log("Made avatar")
  }

  getTouchPrimitive(): TouchPrimitive {
    return this.touchPrimitive
    }

  async init(config: {
    location: Vector3,
    networkServices: NetworkServices,
    threeService: ThreeService,
    surfaceService: SurfaceService,
    stateServices: StateServices,
    userId: string
  }) {
    this.networkServices = config.networkServices
    this.threeService = config.threeService
    this.stateServices = config.stateServices
    this.touchPrimitive = new TouchPrimitive( {
      left:0, top:0, width:100, height: 100
    } )
    this.userId = config.userId
    // const user = await this.networkServices.userService.getUser(config.userId)

    // const b64 = await this.getBse64ForFileId(user.profile_image_file_id)
    let texture = new Texture()
    // if (b64) {
    //   texture = await this.textureFromBase64(b64, 512)
    // }

    const loader = new GLTFLoader()
    loader.load(
      'assets/models/user-avatar2.glb', // exported from Blender
      (gltf) => {
        const model = gltf.scene;

        const ball = model.getObjectByName('capsule-1') as Mesh;
        this.ball = ball
        const container = new Group();
        ball.rotation.set(0,0,0)
        ball.position.set(0,0,0)
        container.add(ball);
        if (ball && ball.isMesh) {
          texture.flipY = false; // GLTF assumes Y-down textures
          const basicmat = new MeshBasicMaterial({
            transparent: true,   // optional if texture has alpha
            color: 0xffffff,
          });
          ball.material = basicmat
        }
        container.position.copy(config.location)
        container.scale.multiplyScalar(3)
        ball.rotateX(degToRad(90))
        this.threeService.addToScene( container );
        this.ballContainer = container
        this.rotate()
        config.surfaceService.__threeInputService.addTouchPrimitiveOwnerToMeshId(
            (ball as Mesh).uuid, this
        )
        this.touchPrimitive.onContextMenu((pointer) => {
          this.onInteract(pointer)
          return true
        })
        this.touchPrimitive.onPressed((pointer) => {
          this.onInteract(pointer)
          return true
        })
        this.waitForUserId()
      },
      (xhr) => {
        // console.log(`Loaded ${(xhr.loaded / xhr.total) * 100}%`)
      },
      (error) => console.error('Error loading GLB:', error)
    );
  }

  onInteract(pointer: PointerTracker) {
    console.log("Mesh got context menu!")
    this.stateServices.store.dispatch(interfaceActions.openContextMenu())
    this.stateServices.router.navigate(
        [{outlets: {'context-menu': "context-profile"}}],
        {
          queryParams: {
            userId: this.userId,
            posX: pointer.pos.x - 100,
            posY: pointer.pos.y - 100
          },
          queryParamsHandling: 'merge' // optional but common
        })
  }

  async updateImage(imageId: string) {
    this.imageFileId = imageId
    await this.waitForUserId()
  }

  async waitForUserId() {
    if (!this.userId) {return}
    if (!this.ball) {return}
    if (!this.ball.isMesh) {return}
    const b64 = await this.getBse64ForFileId(this.imageFileId)
    let texture = new Texture()
    if (b64) {
      try {
        texture = await this.textureFromBase64(b64, 512)
      } catch (e) {
        console.log("Error fetching texture", e)
        return null
      }
    }
    if (this.ball && this.ball.isMesh) {
      // const mat = capsule.material as MeshStandardMaterial
      texture.flipY = false; // GLTF assumes Y-down textures
      const basicmat = new MeshBasicMaterial({
        map: texture,        // your base texture
        transparent: true,   // optional if texture has alpha
      });
      (this.ball.material as Material).dispose()
      this.ball.material = basicmat
      this.ball.material.needsUpdate = true
    }
  }

  async updatePosition(target: Vector3) {
    if (!this.ballContainer) {
      return;
    }
    // maps z in [700,1000] → opacity in [1,0.2]
    const minZ = 1500;
    const maxZ = 7000;
    const minOpacity = 0.2;
    const maxOpacity = 1;

    const opacity = maxOpacity - ((target.z - minZ) / (maxZ - minZ)) * (maxOpacity - minOpacity);

// clamp to 0..1 just in case
    const clampedOpacity = Math.max(minOpacity, Math.min(1, opacity));
    (this.ball.material as Material).opacity = clampedOpacity
    animate(this.ballContainer.position, {
      x: target.x,
      y: target.y - 2,
      z: Math.max(Math.min(target.z, 700), 300),
      duration: 100,
      ease: 'easeOut(3)'
    });
  }

  async rotate() {
    animate(this.pointAngle, {
      angle: degToRad(360),
      duration: 400 + Math.random() * 1000,
      loop: true,
      ease: 'linear',
      onRender: () => {
        this.pointAngle.axis.x = Math.sin(this.pointAngle.angle)
        this.pointAngle.axis.y = Math.cos(this.pointAngle.angle)
        this.ballContainer.setRotationFromAxisAngle(this.pointAngle.axis, degToRad(10))
      }
    });
  }

  async getBse64ForFileId(fileId: string): Promise<string> {
    const imageFile = await this.networkServices.fileService.getFile(
      fileId,
    );
    return imageFile?.fileData ?? ""
  }

  async textureFromBase64(base64: string, maxSize = 512): Promise<Texture> {
    const res = await fetch(base64);         // fetch decodes base64
    const blob = await res.blob();           // turn into Blob
    const bitmap = await createImageBitmap(blob);
      const aspect = bitmap.width / bitmap.height;
    let targetWidth = maxSize;
    let targetHeight = maxSize;

    if (aspect > 1) {
      // Wider than tall → width hits max, scale height down
      targetHeight = Math.round(maxSize / aspect);
    } else {
      // Taller than wide → height hits max, scale width down
      targetWidth = Math.round(maxSize * aspect);
    }
    const resized = await createImageBitmap(blob, {
      resizeWidth: targetWidth,
      resizeHeight: targetHeight,
      resizeQuality: "high",
    });
    const texture = new Texture(resized);
    texture.needsUpdate = true;

    // GPU-friendly defaults
    texture.minFilter = LinearMipMapLinearFilter;
    texture.magFilter = LinearFilter;
    texture.generateMipmaps = true;
    return texture 
  }
}
